import {
  IonAvatar,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
} from "@ionic/react";
import { personCircle as personIcon } from "ionicons/icons";
import React, { Dispatch, SetStateAction } from "react";

import EditPasswordField from "../../atoms/EditPasswordField";
import ModalConfirm from "../../molecules/ModalConfirm";
import { IClass, IFile } from "../../state";
import {
  IMAGE_VALIDATION_MESSAGES,
  MAX_IMAGE_SIZE_MB,
} from "../../constants/upload";
import { ImageValidationError, validateImageFile } from "../../libs/Util";

import styles from "./TeacherProfileForm.module.scss";

export interface TeacherProfileFormProps {
  calling: boolean;
  error: string;
  school_name: string;
  classes: IClass[];
  first_name: string;
  last_name: string;
  photo: IFile | null;
  isUpdating: boolean;
  password: string;
  isInputPasswordMode: boolean;
  setPasswordManager: Dispatch<
    SetStateAction<{
      password: string;
      isInputPasswordMode: boolean;
    }>
  >;
  update: (password: string, photo: IFile | null) => void;
  cancel: () => void;
}

export const TeacherProfileForm = ({
  calling,
  error,
  school_name,
  classes,
  first_name,
  last_name,
  photo,
  isUpdating,
  password,
  isInputPasswordMode,
  setPasswordManager,
  update,
  cancel,
}: TeacherProfileFormProps) => {
  const [state, setState] = React.useState({
    photo,
    errorMessages: {} as { [key: string]: string },
    error,
    imageValidationError: null as ImageValidationError,
  });
  const photoInput = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    setState(s => ({ ...s, error }));
  }, [error]);

  React.useEffect(() => {
    setState(s => ({
      ...s,
      photo,
    }));
  }, [photo]);

  const onChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const key = e.currentTarget.name;
      const val = e.currentTarget.value;
      const ret =
        val.length < 6 || val.length > 32
          ? "パスワードは6文字以上32文字以内で入力してください。"
          : "";
      setState(prevState => ({
        ...prevState,
        errorMessages: { ...prevState.errorMessages, [key]: ret },
      }));
      setPasswordManager(pm => ({
        ...pm,
        password: val,
      }));
    },
    [setPasswordManager],
  );

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (
        password ||
        state.photo?.blob ||
        (state.photo?.id && state.photo?.deleted)
      ) {
        update(password, state.photo);
      } else {
        cancel();
      }
    },
    [state, password, update, cancel],
  );

  return (
    <div className={styles.wrapper}>
      <IonList className={styles.profileList}>
        <IonItem>
          <input
            ref={photoInput}
            name="photo"
            type="file"
            style={{ display: "none" }}
            accept=".png,.jpg,.jpeg"
            onChange={(e: any) => {
              const file = e.currentTarget.files[0];
              if (!file) {
                return;
              }
              const validationError = validateImageFile(
                file,
                MAX_IMAGE_SIZE_MB,
              );
              if (validationError) {
                if (photoInput.current) photoInput.current.value = "";
                setState(s => ({
                  ...s,
                  imageValidationError: validationError,
                }));
                return;
              }
              setState({
                ...state,
                photo: {
                  ...(photo as IFile),
                  name: file.name,
                  url: URL.createObjectURL(file),
                  blob: file,
                  deleted: false,
                },
              });
            }}
          />
          <IonLabel>
            {state.photo?.url ? (
              <IonAvatar>
                <img alt="プロフィール画像" src={state.photo.url} />
              </IonAvatar>
            ) : (
              <IonIcon
                icon={personIcon}
                slot="start"
                size="large"
                color="light"
              />
            )}
          </IonLabel>
          <IonButton
            className={styles.changeButton}
            fill="outline"
            onClick={() => {
              photoInput.current && photoInput.current.click();
            }}
          >
            変更する
          </IonButton>
          {state.photo && state.photo.url && (
            <IonButton
              className={styles.deleteButton}
              fill="outline"
              slot="end"
              onClick={() => {
                (photoInput.current as HTMLInputElement).value = "";
                setState({
                  ...state,
                  photo: {
                    ...(state.photo as IFile),
                    url: "",
                    blob: undefined,
                    deleted: true,
                  },
                });
              }}
            >
              削除する
            </IonButton>
          )}
        </IonItem>
        <EditPasswordField
          isInputPasswordMode={isInputPasswordMode}
          value={password}
          error={state.errorMessages["password"]}
          changeInputMode={() =>
            setPasswordManager(pm => ({
              ...pm,
              isInputPasswordMode: true,
            }))
          }
          onChange={onChange}
          cancel={() =>
            setPasswordManager(pm => ({
              ...pm,
              isInputPasswordMode: false,
              password: "",
            }))
          }
        />
        <IonItem>
          <IonLabel slot="start">学校名</IonLabel>
          <IonLabel className={styles.attributeValue} slot="end">
            {school_name}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel slot="start">クラス</IonLabel>
          <IonLabel className={styles.attributeValue} slot="end">
            {classes.map(cls => (
              <span key={cls.id}>{cls.full_name} </span>
            ))}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel slot="start">名前</IonLabel>
          <IonLabel className={styles.attributeValue} slot="end">
            {last_name + " " + first_name}
          </IonLabel>
        </IonItem>
      </IonList>
      <IonButton
        className={styles.postButton}
        strong
        expand="block"
        disabled={isUpdating || calling}
        onClick={onSubmit}
      >
        更新
      </IonButton>
      <IonButton
        className={styles.cancelButton}
        strong
        expand="block"
        fill="outline"
        onClick={() => cancel()}
      >
        キャンセル
      </IonButton>
      <ModalConfirm
        whiteMode
        isOpen={state.imageValidationError !== null}
        message={
          state.imageValidationError
            ? IMAGE_VALIDATION_MESSAGES[state.imageValidationError]
            : ""
        }
        buttons={[
          {
            title: "OK",
            type: "None",
            action: () => setState(s => ({ ...s, imageValidationError: null })),
          },
        ]}
        onClose={() => setState(s => ({ ...s, imageValidationError: null }))}
      />
    </div>
  );
};

export default TeacherProfileForm;
